// src/components/ClimateResultsView.jsx
import React from 'react';
import { useSunPath } from '../utils/sunPathStore';
import { useWind } from '../utils/windStore';
import { useTemperature } from '../utils/temperatureStore';

/**
 * ClimateResultsView — READ-ONLY results of the climate analyses, for the
 * Design Analysis drawer.
 *
 * Deliberately shows data only: the controls stay in the right pane. That is
 * not just a layout preference — SunPathPanel/WindPanel/TemperaturePanel call
 * deactivate() on unmount, so mounting a second interactive copy here would let
 * closing one view kill the other's 3D visualisation. The store hooks used here
 * only subscribe; they never activate or deactivate a scene.
 *
 * Charts are inline SVG, single-series each, drawn from a palette validated for
 * this app's dark surface (#1a2234): warm = solar/thermal, cool = wind.
 * Every bar is directly labelled, so identity never rests on colour alone.
 */

const C = {
  solar: '#d95926',   // categorical slot 2 (dark step) — sun & thermal
  wind:  '#3987e5',   // slot 1 — wind
  accent:'var(--accent)',   // slot 3 — reserved for a third series if ever needed
  grid:  'rgba(154,167,192,0.18)',
  text:  'var(--text,#e6ebf5)',
  dim:   'var(--text-dim,#9aa7c0)',
  border:'var(--border,#3a4456)',
  surface:'var(--surface-2,#1a2234)',
};

const FACES = [
  { key: 'N', name: 'North' }, { key: 'E', name: 'East' },
  { key: 'S', name: 'South' }, { key: 'W', name: 'West' }, { key: 'R', name: 'Roof' },
];

export default function ClimateResultsView({ project }) {
  const sun = useSunPath();
  const wind = useWind();
  const temp = useTemperature();

  const any = sun.results || wind.results || temp.results || temp.climate;
  if (!any) {
    return (
      <Empty>
        No climate analysis has been run for this project yet. Open
        <b> Climate Analysis </b> in the right panel and run Sun Path, Wind Path
        or Temperature — the results will appear here.
      </Empty>
    );
  }

  return (
    <div style={S.wrap}>
      <SunCard r={sun.results} />
      <WindCard r={wind.results} />
      <TempCard results={temp.results} climate={temp.climate} />
      <div style={S.foot}>
        Screening-grade. Wind is a geometric exposure model, not CFD; thermal
        figures come from a simplified load model. Indicative for design
        decisions, not a substitute for simulation.
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- Sun ---- */
function SunCard({ r }) {
  if (!r) return <Pending icon="☀️" title="Sun Path" what="direct-sun hours per facade" />;
  const rows = FACES.map((f) => ({ label: f.name, value: r.totals[f.key] ?? 0 }));
  const hi = FACES.find((f) => f.key === r.hi)?.name;
  const lo = FACES.find((f) => f.key === r.lo)?.name;
  return (
    <Card icon="☀️" title="Sun Path" sub={r.month ? `${r.month} · direct sun` : 'direct sun'}>
      <Stats items={[
        { label: 'Most sun', value: hi, hint: `${(r.totals[r.hi] ?? 0).toFixed(1)} h/day` },
        { label: 'Least sun', value: lo, hint: `${(r.totals[r.lo] ?? 0).toFixed(1)} h/day` },
      ]} />
      <BarChart rows={rows} color={C.solar} unit="h" decimals={1} caption="Direct-sun hours per day, by facade" />
    </Card>
  );
}

/* --------------------------------------------------------------- Wind ---- */
const ROLE = { windward: 'Windward', leeward: 'Leeward', side: 'Side' };
function WindCard({ r }) {
  if (!r) return <Pending icon="🌬️" title="Wind Path" what="facade exposure and prevailing wind" />;
  const rows = FACES.map((f) => ({
    label: f.name,
    value: r.totals[f.key] ?? 0,
    tag: ROLE[r.roles?.[f.key]] || 'Side',
  }));
  const hi = FACES.find((f) => f.key === r.hi)?.name;
  const lo = FACES.find((f) => f.key === r.lo)?.name;
  return (
    <Card icon="🌬️" title="Wind Path" sub={r.month ? `${r.month} · prevailing wind` : 'prevailing wind'}>
      <Stats items={[
        { label: 'Direction', value: dirLabel(r.dir), hint: `${(+r.speed || 0).toFixed(1)} m/s` },
        { label: 'Most exposed', value: hi },
        { label: 'Most sheltered', value: lo },
      ]} />
      <BarChart rows={rows} color={C.wind} unit="" decimals={2} caption="Relative exposure by facade" />
    </Card>
  );
}

/* -------------------------------------------------------- Temperature ---- */
function TempCard({ results, climate }) {
  if (!results && !climate) {
    return <Pending icon="🌡️" title="Temperature" what="solar gain, UHI and monthly climate" />;
  }
  const months = climate?.perMonth || [];
  const rows = results
    ? [...FACES.filter((f) => f.key !== 'R').map((f) => ({ label: f.name, value: results.perCard?.[f.key] ?? 0 })),
       { label: 'Roof', value: results.roofKWhm2Month ?? 0 }]
    : [];
  const hiName = results ? (FACES.find((f) => f.key === results.hi)?.name) : null;

  return (
    <Card icon="🌡️" title="Temperature" sub="solar gain & site climate">
      {results && (
        <Stats items={[
          { label: 'Hottest facade', value: hiName },
          { label: 'Coolest facade', value: FACES.find((f) => f.key === results.lo)?.name },
          results.uhi ? {
            label: 'Urban heat island',
            value: `+${results.uhi.deltaT.toFixed(1)} °C`,
            hint: `${(results.uhi.builtFrac * 100).toFixed(0)}% built`,
          } : null,
        ].filter(Boolean)} />
      )}

      {rows.length > 0 && (
        <BarChart rows={rows} color={C.solar} unit=" kWh/m²" decimals={0}
                  caption="Monthly solar gain by surface" />
      )}

      {months.length > 1 && <TempRange months={months} />}
    </Card>
  );
}

/** Min–max band + mean line. A range over time is one band, not three lines. */
function TempRange({ months }) {
  const W = 320, H = 96, PT = 10, PB = 20, PL = 26, PR = 8;
  const mins = months.map((m) => m.tMin ?? m.tMean ?? 0);
  const maxs = months.map((m) => m.tMax ?? m.tMean ?? 0);
  const means = months.map((m) => m.tMean ?? 0);
  const lo = Math.floor(Math.min(...mins) / 5) * 5;
  const hi = Math.ceil(Math.max(...maxs) / 5) * 5;
  const span = Math.max(1, hi - lo);
  const x = (i) => PL + (i * (W - PL - PR)) / Math.max(1, months.length - 1);
  const y = (v) => PT + (1 - (v - lo) / span) * (H - PT - PB);

  const band = [
    ...maxs.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`),
    ...mins.map((v, i) => `L${x(mins.length - 1 - i).toFixed(1)},${y(mins[mins.length - 1 - i]).toFixed(1)}`),
    'Z',
  ].join(' ');
  const line = means.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');

  return (
    <div style={{ marginTop: 10 }}>
      <div style={S.caption}>Monthly outdoor temperature — daily min/max range, mean line</div>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} role="img"
           aria-label={`Monthly temperature from ${lo} to ${hi} degrees Celsius`}>
        {[lo, Math.round((lo + hi) / 2), hi].map((t) => (
          <g key={t}>
            <line x1={PL} x2={W - PR} y1={y(t)} y2={y(t)} stroke={C.grid} strokeWidth="1" />
            <text x={PL - 5} y={y(t) + 3} textAnchor="end" fontSize="8" fill={C.dim}>{t}</text>
          </g>
        ))}
        <path d={band} fill={C.solar} opacity="0.22" />
        <path d={line} fill="none" stroke={C.solar} strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round" />
        {months.map((m, i) =>
          i % 2 === 0 ? (
            <text key={i} x={x(i)} y={H - 6} textAnchor="middle" fontSize="8" fill={C.dim}>
              {String(m.month).slice(0, 3)}
            </text>
          ) : null
        )}
      </svg>
    </div>
  );
}

/* --------------------------------------------------------- primitives ---- */
function BarChart({ rows, color, unit = '', decimals = 1, caption }) {
  const max = Math.max(0.0001, ...rows.map((r) => r.value));
  return (
    <div style={{ marginTop: 8 }}>
      {caption && <div style={S.caption}>{caption}</div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        {rows.map((r) => (
          <div key={r.label} style={S.barRow} title={`${r.label}: ${r.value.toFixed(decimals)}${unit}`}>
            <span style={S.barLabel}>{r.label}</span>
            <div style={S.track}>
              <div style={{
                width: `${Math.max(1.5, (r.value / max) * 100)}%`,
                height: '100%', background: color,
                borderTopRightRadius: 4, borderBottomRightRadius: 4,
              }} />
            </div>
            {/* value is direct-labelled, so the bar never relies on colour alone */}
            <span style={S.barValue}>{r.value.toFixed(decimals)}{unit}</span>
            {r.tag && <span style={S.barTag}>{r.tag}</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

function Stats({ items }) {
  return (
    <div style={S.stats}>
      {items.map((it) => (
        <div key={it.label} style={S.stat}>
          <div style={S.statLabel}>{it.label}</div>
          <div style={S.statValue}>{it.value ?? '—'}</div>
          {it.hint && <div style={S.statHint}>{it.hint}</div>}
        </div>
      ))}
    </div>
  );
}

const Card = ({ icon, title, sub, children }) => (
  <section style={S.card}>
    <header style={S.head}>
      <span style={S.icon} aria-hidden="true">{icon}</span>
      <span style={S.title}>{title}</span>
      <span style={S.sub}>{sub}</span>
    </header>
    {children}
  </section>
);

const Pending = ({ icon, title, what }) => (
  <section style={{ ...S.card, opacity: 0.72 }}>
    <header style={S.head}>
      <span style={S.icon} aria-hidden="true">{icon}</span>
      <span style={S.title}>{title}</span>
      <span style={S.sub}>not run yet</span>
    </header>
    <div style={S.pending}>Run it in Climate Analysis to see {what} here.</div>
  </section>
);

const Empty = ({ children }) => (
  <div style={{ fontSize: 12, color: C.dim, lineHeight: 1.6, maxWidth: 420 }}>{children}</div>
);

function dirLabel(deg) {
  if (deg == null) return '—';
  const pts = ['N','NNE','NE','ENE','E','ESE','SE','SSE','S','SSW','SW','WSW','W','WNW','NW','NNW'];
  return pts[Math.round((((+deg % 360) + 360) % 360) / 22.5) % 16];
}

const S = {
  wrap: { display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 460 },
  card: { background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: 12 },
  head: { display: 'flex', alignItems: 'baseline', gap: 7, marginBottom: 8 },
  icon: { fontSize: 15, lineHeight: 1 },
  title: { fontSize: 13, fontWeight: 600, color: C.text },
  sub: { fontSize: 10.5, color: C.dim },
  stats: { display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 4 },
  stat: { flex: '1 1 96px', background: 'var(--surface-2)', border: `1px solid ${C.border}`, borderRadius: 8, padding: '6px 9px' },
  statLabel: { fontSize: 9.5, color: C.dim, textTransform: 'uppercase', letterSpacing: 0.4 },
  statValue: { fontSize: 15, fontWeight: 600, color: C.text, lineHeight: 1.25 },
  statHint: { fontSize: 10, color: C.dim },
  caption: { fontSize: 10, color: C.dim, marginBottom: 5 },
  barRow: { display: 'flex', alignItems: 'center', gap: 7 },
  barLabel: { width: 46, fontSize: 11, color: C.text, flexShrink: 0 },
  track: { flex: 1, height: 9, background: 'var(--surface-2)', borderRadius: 4, overflow: 'hidden' },
  barValue: { width: 62, textAlign: 'right', fontSize: 11, fontWeight: 600, color: C.text, flexShrink: 0 },
  barTag: { width: 58, fontSize: 9.5, color: C.dim, flexShrink: 0 },
  pending: { fontSize: 11, color: C.dim, lineHeight: 1.5 },
  foot: { fontSize: 9.5, color: C.dim, fontStyle: 'italic', lineHeight: 1.45 },
};
